<template>
  <b-modal
    id="create-user-role-modal"
    ref="createRoleModal"
    :title="editMode ? 'Edit Role' : 'Create Role'"
    size="lg"
    hide-footer
    no-close-on-backdrop
    @hidden="resetForm"
  >
    <ValidationObserver v-slot="{ passes }">
      <form @submit.prevent="passes(submitForm)" autocomplete="off">
        <div class="row">
          <!-- Role Name -->
          <div class="col-md-12">
            <ValidationProvider
              name="Role Name"
              rules="required"
              v-slot="{ errors }"
            >
              <div class="form-group">
                <label for="rolename">
                  Role Name
                  <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="rolename"
                  v-model="formData.rolename"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                  placeholder="Enter role name"
                />
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>

        <!-- API Error Message -->
        <div v-if="apiError" class="alert alert-danger" role="alert">
          {{ apiError }}
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end mt-4">
          <b-button
            variant="outline-secondary"
            size="lg"
            class="mr-3"
            @click="hideModal"
            :disabled="isSubmitting"
          >
            Cancel
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            size="lg"
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting">
              <b-spinner small></b-spinner>
              {{ editMode ? 'Updating...' : 'Creating...' }}
            </span>
            <span v-else>{{ editMode ? 'Update Role' : 'Create Role' }}</span>
          </b-button>
        </div>
      </form>
    </ValidationObserver>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { POST_ROLE, UPDATE_ROLE } from "../../../store/actions.type.js";

export default {
  name: "CreateRoleModal",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
    roleToEdit: {
      type: Object,
      default: null,
    },
  },
  watch: {
    roleToEdit: {
      handler(newVal) {
        if (this.editMode && newVal) {
          this.formData.rolename = newVal.role || newVal.name || "";
        }
      },
      immediate: true,
    },
    editMode: {
      handler(newVal) {
        if (!newVal) {
          this.resetForm();
        }
      },
    },
  },
  data() {
    return {
      formData: {
        rolename: "",
      },
      isSubmitting: false,
      apiError: null,
    };
  },
  methods: {
    show() {
      this.$refs.createRoleModal.show();
    },

    hideModal() {
      this.$refs.createRoleModal.hide();
    },

    resetForm() {
      this.formData = {
        rolename: "",
      };
      this.apiError = null;
      this.isSubmitting = false;
    },

    async submitForm() {
      this.isSubmitting = true;
      this.apiError = null;
      if (this.editMode) {
        await this.updateRole();
      } else {
        await this.createRole();
      }
      this.isSubmitting = false;
    },

    async createRole() {
      try {
        const payload = {
          name: this.formData.rolename,
        };
        const response = await this.$store.dispatch(POST_ROLE, payload);

        if (response.data && response.data.code === "1000-013") {
          this.$toast.success(
            response.data.message || "User role created successfully!",
            { position: "top-right" },
          );
          this.$emit("user-role-created");
          this.hideModal();
        } else {
          const errorMessage =
            response.data?.message ||
            "Failed to create user role. Please try again.";
          const errorDetail = response.data?.detail;
          this.apiError = errorDetail
            ? `${errorMessage}: ${errorDetail}`
            : errorMessage;
          this.$toast.error(this.apiError, { position: "top-right" });
        }
      } catch (error) {
        console.error("Error creating user:", error);
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred while creating the user. Please try again.";
        const errorDetail = error.response?.data?.detail;
        this.apiError = errorDetail
          ? `${errorMessage}: ${errorDetail}`
          : errorMessage;
        this.$toast.error(this.apiError, { position: "top-right" });
      }
    },

    async updateRole() {
      try {
        // Use your update API here, e.g. UPDATE_ROLE
        const payload = { id: this.roleToEdit.roleid, name: this.formData.rolename };
        const response = await this.$store.dispatch(UPDATE_ROLE, payload);
        if (response.data && response.data.code === "1000-013") {
          this.$toast.success(
            response.data.message || "User role updated successfully!",
            { position: "top-right" },
          );
          this.$emit("user-role-created");
          this.hideModal();
        } else {
          const errorMessage =
            response.data?.message ||
            "Failed to update user role. Please try again.";
          const errorDetail = response.data?.detail;
          this.apiError = errorDetail
            ? `${errorMessage}: ${errorDetail}`
            : errorMessage;
          this.$toast.error(this.apiError, { position: "top-right" });
        }
      } catch (error) {
        console.error("Error updating user:", error);
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred while updating the user. Please try again.";
        const errorDetail = error.response?.data?.detail;
        this.apiError = errorDetail
          ? `${errorMessage}: ${errorDetail}`
          : errorMessage;
        this.$toast.error(this.apiError, { position: "top-right" });
      }
    },
  },
};
</script>

<style scoped>
.error-msg {
  font-size: 0.875rem;
  display: block;
  margin-top: 0.25rem;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.custom-control-label {
  font-weight: normal;
}
</style>
